/**
 * My Connectors Settings Page
 *
 * Personal settings for every signed-in user (Settings → My Account → My Connectors):
 * - BYOK Claude / ChatGPT keys (browser localStorage) — required for whiteboard photo import
 * - Per-user Miro / Jira credentials (override workspace defaults)
 *
 * Route: /prioritization/settings/my-connectors
 */

import { useState, useEffect } from 'react';
import {
  Plug,
  Check,
  X,
  Eye,
  EyeOff,
  Loader2,
  RefreshCw,
  Info,
  Plus,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import {
  listConnectorConfigs,
  saveUserConnectorCredentials,
  clearConnectorCredentials,
  type ConnectorConfigRow,
  type ConnectorStatus,
} from '../../../lib/connectorConfigsService';
import type { ConnectorType } from '../../../lib/connectors';
import { MyAiProviderKeysSection } from '../../../components/prioritization/settings/MyAiProviderKeysSection';
import { getIntegrationsApi, type SlackChannel } from '../../../lib/integrationsApi';
import { Link } from 'react-router-dom';

// ── Connector definitions (subset that supports user-level auth) ───────────────

interface UserConnectorDef {
  type: ConnectorType;
  label: string;
  description: string;
  scope: 'hybrid' | 'user-only';
  /** 'token' = enter credentials in a form; 'oauth' = redirect to the provider. */
  kind: 'token' | 'oauth';
  icon: React.ReactNode;
  /** Only used for `kind: 'token'`. */
  fields?: { key: string; label: string; placeholder: string; secret?: boolean }[];
}

const MIRO_ICON = (
  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="#FFD02F">
    <path d="M17.392 2H13.33l3.622 5.913L12.44 2H8.378l3.344 7.007L7.44 2H3.377L7.44 12 3.377 22h4.063l4.282-7.007L8.379 22h4.062l4.282-9.007L20.606 22h4.017L17.392 12l7.23-10H17.392z" />
  </svg>
);

const JIRA_ICON = (
  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="#0052CC">
    <path d="M11.571 11.429 5.714 5.571l5.857 5.858-5.857 5.857 5.857-5.857zm1.143-5.858L6.857 12l5.857 5.857L18.571 12l-5.857-5.857z" />
  </svg>
);

const DROPBOX_ICON = (
  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="#0061FF">
    <path d="M8 5.5L4.5 8 8 10.5 11.5 8 8 5.5zM16 5.5L12.5 8 16 10.5 19.5 8 16 5.5zM4.5 13L8 15.5 11.5 13 8 10.5 4.5 13zM16 10.5L12.5 13 16 15.5 19.5 13 16 10.5zM8 16.2l3.5 2.3 3.5-2.3-3.5-2.2L8 16.2z" />
  </svg>
);

const GOOGLE_DOCS_ICON = (
  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none">
    <path d="M7 4h7l4 4v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" fill="#4285F4" />
    <path d="M14 4v4h4" fill="none" stroke="#2563EB" strokeWidth="0.75" />
    <rect x="9" y="10" width="6" height="1" rx="0.5" fill="white" />
    <rect x="9" y="12.5" width="6" height="1" rx="0.5" fill="white" />
    <rect x="9" y="15" width="4" height="1" rx="0.5" fill="white" />
  </svg>
);

const USER_CONNECTOR_DEFS: UserConnectorDef[] = [
  {
    type: 'miro',
    label: 'Miro',
    description: 'Use your personal Miro account instead of the workspace shared token.',
    scope: 'hybrid',
    kind: 'token',
    icon: MIRO_ICON,
    fields: [
      { key: 'accessToken', label: 'Personal Access Token', placeholder: 'miro_...', secret: true },
      { key: 'teamId', label: 'Team ID (optional)', placeholder: 'Team ID or leave blank' },
    ],
  },
  {
    type: 'jira',
    label: 'Jira',
    description: 'Use your own Jira API token so issues are attributed to your user.',
    scope: 'hybrid',
    kind: 'token',
    icon: JIRA_ICON,
    fields: [
      { key: 'siteUrl', label: 'Site URL', placeholder: 'https://yourorg.atlassian.net' },
      { key: 'email', label: 'Email', placeholder: 'you@example.com' },
      { key: 'apiToken', label: 'API Token', placeholder: 'Your Jira API token', secret: true },
    ],
  },
  {
    type: 'dropbox',
    label: 'Dropbox',
    description: 'Connect your own Dropbox so AI reads your files. Overrides the workspace-shared connection for you.',
    scope: 'hybrid',
    kind: 'oauth',
    icon: DROPBOX_ICON,
  },
  {
    type: 'google-docs',
    label: 'Google Docs',
    description: 'Connect your own Google Docs so AI reads your documents. Overrides the workspace-shared connection for you.',
    scope: 'hybrid',
    kind: 'oauth',
    icon: GOOGLE_DOCS_ICON,
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function deriveStatus(row: ConnectorConfigRow | null | undefined): ConnectorStatus {
  if (!row || !row.isActive) return 'disabled';
  return Object.keys(row.authData).length > 0 ? 'active' : 'available';
}

// ── Credential form for one connector ─────────────────────────────────────────

interface ConnectorFormProps {
  def: UserConnectorDef;
  workspaceRow: ConnectorConfigRow | null;
  userRow: ConnectorConfigRow | null;
  onSave: (type: ConnectorType, values: Record<string, string>) => Promise<void>;
  onClear: (type: ConnectorType) => Promise<void>;
}

function ConnectorForm({ def, workspaceRow, userRow, onSave, onClear }: ConnectorFormProps) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries((def.fields ?? []).map((f) => [f.key, (userRow?.authData?.[f.key] as string) ?? '']))
  );
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsStatus = deriveStatus(workspaceRow);
  const userStatus = deriveStatus(userRow);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await onSave(def.type, values);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    setClearing(true);
    setError(null);
    try {
      await onClear(def.type);
      setValues(Object.fromEntries((def.fields ?? []).map((f) => [f.key, ''])));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to clear');
    } finally {
      setClearing(false);
    }
  }

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 mt-0.5">{def.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-[var(--text-pri)]">{def.label}</h3>
            {userStatus === 'active' && (
              <span className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border border-[var(--green)] px-2 py-0.5 rounded-full">
                <Check className="h-3 w-3" /> Personal credentials saved
              </span>
            )}
            {userStatus !== 'active' && wsStatus === 'active' && (
              <span className="flex items-center gap-1 text-xs text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] px-2 py-0.5 rounded-full">
                <Info className="h-3 w-3" /> Using workspace default
              </span>
            )}
            {userStatus !== 'active' && wsStatus !== 'active' && wsStatus !== 'disabled' && (
              <span className="flex items-center gap-1 text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] px-2 py-0.5 rounded-full">
                <Info className="h-3 w-3" /> Workspace not yet configured
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--text-ter)] mt-0.5">{def.description}</p>
        </div>
      </div>

      <div className="space-y-3">
        {(def.fields ?? []).map((field) => (
          <div key={field.key}>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">{field.label}</label>
            <div className="relative">
              <input
                type={field.secret && !showSecret[field.key] ? 'password' : 'text'}
                value={values[field.key] ?? ''}
                onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                placeholder={field.placeholder}
                className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-10"
              />
              {field.secret && (
                <button
                  type="button"
                  onClick={() => setShowSecret((prev) => ({ ...prev, [field.key]: !prev[field.key] }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                >
                  {showSecret[field.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <p className="mt-2 text-xs text-[var(--coral)]">{error}</p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          {saved ? 'Saved!' : 'Save My Credentials'}
        </button>

        {userStatus === 'active' && (
          <button
            onClick={handleClear}
            disabled={clearing}
            className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] border border-[var(--coral)] transition-colors disabled:opacity-50"
          >
            {clearing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
            Remove My Credentials
          </button>
        )}
      </div>

      {wsStatus === 'active' && (
        <p className="mt-3 text-xs text-[var(--text-ter)] flex items-center gap-1">
          <Info className="h-3 w-3" />
          A workspace default is configured. Your personal credentials will take precedence when set.
        </p>
      )}
    </div>
  );
}

// ── Slack personal notify preference ───────────────────────────────────────────

const SLACK_ICON = (
  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

function SlackNotifyPreferenceSection({
  workspaceId,
  userId,
}: {
  workspaceId: string | null;
  userId: string | null;
}) {
  const [connected, setConnected] = useState(false);
  const [teamName, setTeamName] = useState<string | null>(null);
  const [channelId, setChannelId] = useState('');
  const [channelName, setChannelName] = useState<string | null>(null);
  const [workspaceDefaultName, setWorkspaceDefaultName] = useState<string | null>(null);
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId || !userId) {
      setLoading(false);
      return;
    }
    const api = getIntegrationsApi(workspaceId);
    Promise.all([
      api.getSlackStatus(userId),
      api.listSlackChannels().catch(() => [] as SlackChannel[]),
    ])
      .then(([status, available]) => {
        setConnected(Boolean(status.connected));
        setTeamName(status.slackTeamName ?? null);
        setChannelId(status.notifyChannelSource === 'user' ? (status.notifyChannelId ?? '') : '');
        setChannelName(
          status.notifyChannelSource === 'user' ? (status.notifyChannelName ?? null) : null,
        );
        setWorkspaceDefaultName(status.workspaceNotifyChannelName ?? null);
        setChannels(available);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load Slack status');
      })
      .finally(() => setLoading(false));
  }, [workspaceId, userId]);

  async function handleSave() {
    if (!workspaceId || !userId || !channelId) return;
    setSaving(true);
    setError(null);
    try {
      const api = getIntegrationsApi(workspaceId);
      const result = await api.setSlackUserNotifyPreference(userId, channelId);
      setChannelName(result.notifyChannelName);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border-t border-[var(--rule)] pt-6 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-[var(--text-pri)] mb-1">Slack notifications</h2>
        <p className="text-sm text-[var(--text-ter)]">
          Choose where <em>you</em> want task notifications. Workspace Slack install is managed in{' '}
          <Link to="/prioritization/settings/connectors" className="text-primary-600 hover:underline">
            Connectors
          </Link>
          .
        </p>
      </div>

      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 mt-0.5 text-[#4A154B]">{SLACK_ICON}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-[var(--text-pri)]">Slack</h3>
              {connected ? (
                <span className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border border-[var(--green)] px-2 py-0.5 rounded-full">
                  <Check className="h-3 w-3" />
                  Workspace connected{teamName ? ` · ${teamName}` : ''}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] px-2 py-0.5 rounded-full">
                  <Info className="h-3 w-3" />
                  Workspace Slack not connected
                </span>
              )}
            </div>
            <p className="text-sm text-[var(--text-ter)] mt-0.5">
              Personal channel for task assignment notifies. Ambient digests use the workspace default.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center text-sm text-[var(--text-ter)]">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading…
          </div>
        ) : !connected ? (
          <p className="text-sm text-[var(--text-sec)]">
            Ask a workspace admin to connect Slack under{' '}
            <Link to="/prioritization/settings/connectors" className="text-primary-600 hover:underline">
              Settings → Connectors
            </Link>
            .
          </p>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                My notification channel
              </label>
              <select
                value={channelId}
                onChange={(e) => setChannelId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select a channel…</option>
                {channels.map((ch) => (
                  <option key={ch.id} value={ch.id}>
                    #{ch.name}{ch.isPrivate ? ' (private)' : ''}
                  </option>
                ))}
              </select>
              {channelName && (
                <p className="mt-1 text-xs text-[var(--text-ter)]">Currently #{channelName}</p>
              )}
              {!channelName && workspaceDefaultName && (
                <p className="mt-1 text-xs text-[var(--text-ter)]">
                  No personal channel yet — falling back to workspace default #{workspaceDefaultName}
                </p>
              )}
            </div>

            {error && <p className="text-xs text-[var(--coral)]">{error}</p>}

            <button
              onClick={handleSave}
              disabled={saving || !channelId}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              {saved ? 'Saved!' : 'Save My Channel'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

// ── OAuth connector card (Dropbox, Google Docs) ─────────────────────────────────

interface OAuthConnectorCardProps {
  def: UserConnectorDef;
  workspaceRow: ConnectorConfigRow | null;
  userRow: ConnectorConfigRow | null;
  workspaceId: string;
  userId: string;
  onDisconnected: (type: ConnectorType) => void;
}

function OAuthConnectorCard({ def, workspaceRow, userRow, workspaceId, userId, onDisconnected }: OAuthConnectorCardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsStatus = deriveStatus(workspaceRow);
  const userStatus = deriveStatus(userRow);

  async function handleConnect() {
    setBusy(true);
    setError(null);
    try {
      const api = getIntegrationsApi(workspaceId);
      const initiate =
        def.type === 'dropbox' ? api.initiateDropboxAuth.bind(api) : api.initiateGoogleDocsAuth.bind(api);
      const res = await initiate({ scope: 'user', userId, redirectUrl: window.location.href });
      if (res.authUrl) {
        window.location.href = res.authUrl;
        return;
      }
      throw new Error(res.error || 'Did not receive an authorization URL');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start connection');
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    setBusy(true);
    setError(null);
    try {
      const api = getIntegrationsApi(workspaceId);
      if (def.type === 'dropbox') await api.disconnectDropbox(userId);
      else await api.disconnectGoogleDocs(userId);
      onDisconnected(def.type);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to disconnect');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 mt-0.5">{def.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-[var(--text-pri)]">{def.label}</h3>
            {userStatus === 'active' && (
              <span className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border border-[var(--green)] px-2 py-0.5 rounded-full">
                <Check className="h-3 w-3" /> Personal connection active
              </span>
            )}
            {userStatus !== 'active' && wsStatus === 'active' && (
              <span className="flex items-center gap-1 text-xs text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] px-2 py-0.5 rounded-full">
                <Info className="h-3 w-3" /> Using workspace connection
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--text-ter)] mt-0.5">{def.description}</p>
        </div>
      </div>

      {error && <p className="mb-2 text-xs text-[var(--coral)]">{error}</p>}

      <div className="flex items-center gap-2">
        {userStatus === 'active' ? (
          <button
            onClick={handleDisconnect}
            disabled={busy}
            className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] border border-[var(--coral)] transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Disconnect my {def.label}
          </button>
        ) : (
          <button
            onClick={handleConnect}
            disabled={busy}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white font-medium transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Connect my {def.label}
          </button>
        )}
      </div>

      {wsStatus === 'active' && userStatus !== 'active' && (
        <p className="mt-3 text-xs text-[var(--text-ter)] flex items-center gap-1">
          <Info className="h-3 w-3" />
          A workspace connection is configured. Connecting your own takes precedence for you.
        </p>
      )}
    </div>
  );
}

export default function MyConnectors() {
  const { userId } = useUserStore();
  const workspaceId = getCurrentWorkspaceId();

  const [rows, setRows] = useState<ConnectorConfigRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId || !userId) { setLoading(false); return; }
    listConnectorConfigs(workspaceId, userId).then(({ data, error }) => {
      if (error) setLoadError(error);
      else setRows(data);
      setLoading(false);
    });
  }, [workspaceId, userId]);

  // Handle the personal OAuth callback (Dropbox / Google Docs connected under
  // the user scope). The engine wrote the personal row; reload from the DB.
  useEffect(() => {
    if (!workspaceId || !userId) return;
    const params = new URLSearchParams(window.location.search);
    const dropbox = params.get('dropbox');
    const googleDocs = params.get('google_docs');
    const scope = params.get('scope');
    if (scope === 'user' && (dropbox === 'connected' || googleDocs === 'connected')) {
      void (async () => {
        await new Promise((r) => setTimeout(r, 400));
        const { data } = await listConnectorConfigs(workspaceId, userId);
        setRows(data);
      })();
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getRow(type: ConnectorType, scope: 'workspace' | 'user'): ConnectorConfigRow | null {
    return rows.find((r) =>
      r.connectorType === type && (scope === 'workspace' ? r.userId === null : r.userId === userId)
    ) ?? null;
  }

  async function handleSave(type: ConnectorType, values: Record<string, string>) {
    if (!workspaceId || !userId) throw new Error('No workspace or user');
    const { data, error } = await saveUserConnectorCredentials(workspaceId, userId, type, values);
    if (error) throw new Error(error);
    if (data) {
      setRows((prev) => {
        const next = prev.filter((r) => !(r.connectorType === type && r.userId === userId));
        return [...next, data];
      });
    }
  }

  async function handleClear(type: ConnectorType) {
    if (!workspaceId || !userId) return;
    await clearConnectorCredentials(workspaceId, userId, type);
    setRows((prev) =>
      prev.map((r) =>
        r.connectorType === type && r.userId === userId ? { ...r, authData: {} } : r
      )
    );
  }

  // OAuth connectors clear their credential through the engine; reflect it locally.
  function handleOAuthDisconnected(type: ConnectorType) {
    setRows((prev) =>
      prev.map((r) =>
        r.connectorType === type && r.userId === userId ? { ...r, authData: {}, isActive: false } : r
      )
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Plug className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-100 dark:bg-primary-900/30"
        title="My Connectors"
        subtitle="Personal AI keys and integration credentials. Stored for you only — not shared workspace-wide."
        actions={
          <button
            onClick={() => {
              setLoading(true);
              setLoadError(null);
              if (workspaceId && userId) {
                listConnectorConfigs(workspaceId, userId).then(({ data, error }) => {
                  if (error) setLoadError(error);
                  else setRows(data);
                  setLoading(false);
                });
              }
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)]"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        }
      />

      <MyAiProviderKeysSection />

      <SlackNotifyPreferenceSection workspaceId={workspaceId} userId={userId} />

      <div className="border-t border-[var(--rule)] pt-6">
        <h2 className="text-base font-semibold text-[var(--text-pri)] mb-1">Integration credentials</h2>
        <p className="text-sm text-[var(--text-ter)] mb-4">
          Miro and Jira support per-user tokens; Dropbox and Google Docs connect your own account via OAuth.
          Yours take precedence over the workspace default when set.
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-[var(--text-ter)]">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading connectors…
        </div>
      )}

      {loadError && (
        <div className="p-4 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
          {loadError}
        </div>
      )}

      {!loading && !loadError && (
        <div className="space-y-4">
          {USER_CONNECTOR_DEFS.map((def) =>
            def.kind === 'oauth' ? (
              <OAuthConnectorCard
                key={def.type}
                def={def}
                workspaceRow={getRow(def.type, 'workspace')}
                userRow={getRow(def.type, 'user')}
                workspaceId={workspaceId ?? ''}
                userId={userId ?? ''}
                onDisconnected={handleOAuthDisconnected}
              />
            ) : (
              <ConnectorForm
                key={def.type}
                def={def}
                workspaceRow={getRow(def.type, 'workspace')}
                userRow={getRow(def.type, 'user')}
                onSave={handleSave}
                onClear={handleClear}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}
